import { useEffect, useMemo, useRef } from 'react';
import {
  THEMES, isBuiltinScene, isComplete, isHunt, pickPicture, picturePool, satisfies,
  type Board, type LevelConfig, type Theme,
} from '@numslearn/shared';
import { useSettings } from '../settings.js';
import { playMatch, playMiss } from '../sound.js';
import { PuzzlePiece } from './PuzzlePicture.js';
import { useMedia } from '../media.js';
import { isLocalPicture } from '../localPictures.js';
import { Doodles } from './Doodles.js';

/**
 * The minimum an effect needs. Declared here rather than importing the solo
 * game's `MatchEvent` so the same board renders a live class round, where the
 * event comes off the wire.
 */
export interface EffectEvent {
  kind: 'match' | 'miss';
  /** One id on a hunt board, two on a pair board. */
  ids: number[];
  nonce: number;
}

interface Props {
  board: Board;
  config: LevelConfig;
  selected: number | null;
  hintWhere: number[] | null;
  lastEvent: EffectEvent | null;
  onTap: (index: number) => void;
  disabled: boolean;
  /** Seeds which built-in picture a puzzle board reveals. */
  seed?: number;
}

/** Playground and Clay cycle a four-colour palette across the grid. */
const paletteVar = (i: number) => `var(--g-c${(i % 4) + 1})`;

export function BoardView({
  board, config, selected, hintWhere, lastEvent, onTap, disabled, seed = 0,
}: Props) {
  const { themeId, num } = useSettings();
  const theme: Theme = THEMES[themeId];
  const wrapRef = useRef<HTMLDivElement>(null);
  /** Local streak, only so the match tone can rise with it. */
  const streak = useRef(0);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastNonce = useRef(0);
  const hunt = isHunt(config);
  const media = useMedia();
  /* Chosen once per board. The pool changes whenever a picture is uploaded,
     and re-drawing then would swap a half-revealed picture for another; so
     the choice is kept until the board (seed) or the level's picture
     changes — or the chosen picture is deleted, which must not leave a hole. */
  const chosen = useRef<{ key: string; id: string } | null>(null);
  const picture = useMemo(() => {
    // Wait for the library: drawing from the built-ins alone and then again
    // once uploads arrive would change the picture under the first pieces.
    if (!config.puzzle || !media.loaded) return null;
    const ids = media.pictures.map((p) => p.id);
    const key = `${seed}|${config.puzzleScene}`;
    const prev = chosen.current;
    const stillThere = prev !== null && (
      isBuiltinScene(prev.id) || ids.includes(prev.id)
      // A teacher's upload on a child's device: not listable here, and already
      // checked by the server when it settled the round's picture.
      || (!isLocalPicture(prev.id) && !media.available)
    );
    if (prev?.key !== key || !stillThere) {
      const id = pickPicture(knownScene(config.puzzleScene, ids, media), seed, picturePool(ids));
      chosen.current = { key, id };
    }
    return media.pictureSrc(chosen.current!.id);
  }, [config.puzzle, config.puzzleScene, seed, media]);
  /* The picture's frame is the grid as dealt. "Add row" appends below it, so
     the dealt tiles never move and every piece keeps its place; the added
     rows are ordinary tiles with no picture under them. */
  const frameRows = Math.min(config.rows, board.rows);
  const frameCells = frameRows * board.cols;
  /* A finished board shows the whole picture. On a pair board that is every
     tile cleared; on a hunt board the decoys are still standing — they stay
     on purpose while the child plays — so at the finish they are lifted too,
     or a hunt would never show its picture. Only then do the seams between
     the pieces close (see `.is-solved` in the stylesheet): anything less and
     the gaps have to stay, because they are what makes an unfinished picture
     read as unfinished. */
  const solved = picture !== null && isComplete(board, config);

  /* Warm the picture without putting it on the page.
     A piece is only mounted when its tile clears, so the first match would
     otherwise wait on a network round-trip for a teacher's photo and reveal an
     empty hole. Decoding it into the cache costs nothing and shows nothing. */
  useEffect(() => {
    if (!picture) return;
    const warm = new Image();
    warm.src = picture;
  }, [picture]);

  /* Match effects. Cleared tiles stay mounted, so their positions are still
     measurable — except when a full row collapsed, where we simply skip. */
  useEffect(() => {
    if (!lastEvent || lastEvent.nonce === lastNonce.current) return;
    lastNonce.current = lastEvent.nonce;
    // Sound first: someone who has asked for less motion still gets feedback.
    if (lastEvent.kind === 'match') { streak.current += 1; playMatch(streak.current); }
    else { streak.current = 0; playMiss(); }
    if (lastEvent.kind !== 'match') return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const spots = lastEvent.ids
      .map((id) => wrap.querySelector<HTMLElement>(`[data-tid="${id}"]`))
      .filter((el): el is HTMLElement => el !== null);
    if (spots.length === 0) return;

    const box = wrap.getBoundingClientRect();
    const centre = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      return { x: r.left - box.left + r.width / 2, y: r.top - box.top + r.height / 2 };
    };
    const points = spots.map(centre);

    // Paper & Marker: a red stroke that draws itself across the pair. A hunt
    // board has one tile, so there is nothing to strike through — it gets the
    // sparks instead.
    if (theme.selectionStyle === 'circle' && points.length === 2) {
      const svg = svgRef.current;
      if (!svg) return;
      const [pa, pb] = points as [{ x: number; y: number }, { x: number; y: number }];
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(pa.x));
      line.setAttribute('y1', String(pa.y));
      line.setAttribute('x2', String(pb.x));
      line.setAttribute('y2', String(pb.y));
      line.setAttribute('class', 'marker');
      line.style.setProperty('--len', String(Math.hypot(pb.x - pa.x, pb.y - pa.y)));
      svg.appendChild(line);
      window.setTimeout(() => line.remove(), 1500);
      return;
    }

    for (const p of points) {
      for (let k = 0; k < 9; k++) {
        const s = document.createElement('span');
        s.className = 'spark';
        const angle = Math.random() * Math.PI * 2;
        const dist = 24 + Math.random() * 50;
        s.style.left = `${p.x - 5}px`;
        s.style.top = `${p.y - 5}px`;
        s.style.background = paletteVar(k);
        s.style.setProperty('--dx', `${(Math.cos(angle) * dist).toFixed(1)}px`);
        s.style.setProperty('--dy', `${(Math.sin(angle) * dist).toFixed(1)}px`);
        if (theme.id === 'clay') s.style.borderRadius = '3px';
        wrap.appendChild(s);
        window.setTimeout(() => s.remove(), 700);
      }
    }
  }, [lastEvent, theme]);

  const missing = lastEvent?.kind === 'miss' ? lastEvent.ids : null;

  return (
    <div
      className={[
        'board-wrap',
        config.puzzle ? 'is-puzzle' : '',
        solved ? 'is-solved' : '',
      ].filter(Boolean).join(' ')}
      ref={wrapRef}
    >
      <Doodles nonce={lastEvent?.nonce ?? 0} enabled={config.decorations && theme.id === 'paper'} />
      <div
        className="board"
        style={{
          gridTemplateColumns: `repeat(${board.cols}, minmax(0, 1fr))`,
          // The stylesheet caps the board from these: a wide board fills the
          // width, a narrow one does not blow its four tiles up to the size of
          // dinner plates, and on a TV the whole board fits the screen height.
          '--cols': String(board.cols),
          // The dealt height, not the current one: rows collapse as they
          // clear, and sizing from what is left would grow every tile each
          // time a row goes. Only "Add row" — which really needs the room —
          // may shrink them.
          '--rows': String(Math.max(config.rows, board.rows)),
          // The grid a piece has to reassemble itself into.
          ...(picture ? { '--pz-cols': String(board.cols), '--pz-rows': String(frameRows) } : null),
        } as React.CSSProperties}
        role="grid"
        aria-label="Number board"
      >
        {board.tiles.map((tile, i) => {
          const isSel = selected === i;
          const isHint = hintWhere?.includes(i) ?? false;
          const isMiss = missing?.includes(tile.id) ?? false;
          return (
            <button
              key={tile.id}
              type="button"
              data-tid={tile.id}
              className={[
                'tile',
                tile.cleared ? 'is-gone' : '',
                picture && i >= frameCells ? 'is-extra' : '',
                solved && !tile.cleared ? 'is-revealed' : '',
                isSel ? 'is-sel' : '',
                isHint ? 'is-hint' : '',
                isMiss ? 'is-miss' : '',
              ].filter(Boolean).join(' ')}
              style={{ '--tile-c': theme.multicolourTiles ? paletteVar(i) : 'var(--g-tile)' } as React.CSSProperties}
              onClick={() => onTap(i)}
              disabled={disabled || tile.cleared}
              aria-label={tile.label}
              // On a hunt board a tile is not "pressed", it is claimed as an
              // answer — so it reports checked, and a screen reader hears the
              // same thing a sighted child sees.
              {...(hunt
                ? { role: 'checkbox' as const, 'aria-checked': tile.cleared }
                : { 'aria-pressed': isSel })}
            >
              {/* Mounted only once the tile is gone: an unsolved board has no
                  picture in it to peek at, in the gaps or with devtools. */}
              {picture && (tile.cleared || solved) && i < frameCells && (
                <PuzzlePiece src={picture} col={i % board.cols} row={Math.floor(i / board.cols)} />
              )}
              {/* The length picks the numeral size: "7" can fill its tile,
                  "12×12" has to share it. */}
              <span className="tile-face" data-len={Math.min(5, tile.label.length)}>
                {/\d/.test(tile.label) ? num(tile.label) : tile.label}
              </span>
            </button>
          );
        })}
      </div>
      <svg className="fx-layer" ref={svgRef} aria-hidden="true" />
    </div>
  );
}

/**
 * The level's named picture, if it can still be shown — otherwise '' and the
 * board draws one by seed.
 *
 * A picture deleted after a level named it must not leave a hole under the
 * board. That can only be judged for pictures this device can list: a
 * child's tablet in a live class cannot list the teacher's uploads, but the
 * server has already checked the round's picture there, so it is trusted.
 */
function knownScene(scene: string, ids: readonly string[], media: ReturnType<typeof useMedia>): string {
  if (!scene || isBuiltinScene(scene) || !media.loaded) return scene;
  if (isLocalPicture(scene)) return ids.includes(scene) ? scene : '';
  return !media.available || ids.includes(scene) ? scene : '';
}

/** Exported for the hunt HUD: how many targets a board started with. */
export const countTargets = (board: Board, cfg: LevelConfig): number =>
  board.tiles.reduce((n, t) => n + (satisfies(t, cfg) && !t.cleared ? 1 : 0), 0);
